import React, { useLayoutEffect, useRef, useState } from 'react';
import Header from '../Header';
import AIAgentScreen from '../../screens/AIAgentScreen';
import { FieldHeader } from './FieldPageChrome';
import { useAuth } from '../../contexts/AuthContext';
import { recordAudience } from '../../lib/record-paths';

/**
 * THE AI ASSISTANT IN A FIELD TREE (fe#815).
 *
 * The header's AI button navigates to /agent, which only the office tree
 * routed, so in both field trees it fell through to the catch-all and did
 * nothing. This page is that route for a field hand: the field tree's own
 * header (the shared <Header />, or FieldHeader in the trucking / rentals shell
 * for its phone-safe bell), then the office's AIAgentScreen below it.
 *
 * AIAgentScreen is `h-full` and pins its composer to the bottom, which needs a
 * parent of definite height. The office gets one from AppLayout's single scroll
 * container; a field tree has none, and the SyncStatusBar sits above the route
 * at a height that changes with its message. So the column is sized from where
 * it actually starts to the bottom of the viewport, measured, rather than a
 * guessed 100vh that would push the composer below the fold by the bar's
 * height.
 */
export default function FieldAgentPage() {
  const { hasVertical } = useAuth();
  const myWork = recordAudience(true, hasVertical) === 'my-work';
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      setHeight(Math.max(0, Math.round(vh - Math.max(0, top))));
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="field-agent-page"
      className="flex flex-col overflow-hidden bg-fieldiq-dark"
      style={{ height: height ?? '100dvh' }}
    >
      {myWork ? <FieldHeader /> : <Header />}
      <div className="min-h-0 flex-1">
        <AIAgentScreen />
      </div>
    </div>
  );
}
